// ── Collateral Spreadsheet — entry/view modal ──────────────────────────────
//
// NOTHING ON THIS FORM IS HIDDEN.
//
// A/R aging and the inventory breakout used to sit behind a ▼ on their own row,
// collapsed by default. The two figures most worth checking on a collateral
// entry — does the aging add up to gross A/R, does the breakout add up to gross
// inventory — were therefore invisible unless someone went looking for them,
// and a number nobody looks at is a number nobody reconciles. They are now part
// of their line, permanently, with the comparison stated rather than left to be
// done in someone's head.
//
// The old layout was one ten-column table that scrolled sideways inside a
// modal, with the detail injected as full-width rows underneath. That works
// while the detail is usually collapsed and falls apart once it never is. Each
// line is now its own card: the calculation chain across the top, the detail
// beneath it, and the line's own collateral value called out — so a line reads
// as one thing instead of a row and two orphaned continuations.
//
// THE MATH IS UNCHANGED. Every figure still comes from calcCollateralLine /
// calcCollateralHeader in shared/collateral.js, which is also what the loan's
// Collateral tab reads. Nothing here recomputes anything — a second definition
// of "collateral value" is exactly the failure this app has produced before.

import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Btn, FInputNum, Modal, Spinner } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { calcCollateralLine, calcCollateralHeader } from "../../../shared/collateral";
import { NotesPanel } from "./NotesPanel";

const COLLATERAL_TYPES = [
  'Inventory', 'Accounts Receivable', 'Machinery and Equipment',
  'Real Estate', 'Letter of Credit'
];

const AR_BUCKETS = [
  ['0–30',   'araging0to30'],
  ['31–60',  'araging31to60'],
  ['61–90',  'araging61to90'],
  ['90+',    'araging90plus'],
  ['120+',   'araging120plus'],
];

const INV_BUCKETS = [
  ['Raw Materials',    'invrawmaterials'],
  ['Work in Progress', 'invworkinprogress'],
  ['Finished Goods',   'invfinishedgoods'],
  ['General',          'invgeneral'],
  ['Other',            'invother'],
];

const AP_BUCKETS = [
  ['0–30',  'apaging0to30'],
  ['31–60', 'apaging31to60'],
  ['61–90', 'apaging61to90'],
  ['90+',   'apaging90plus'],
];

// ── styles ────────────────────────────────────────────────────────────────
// At module scope rather than rebuilt on every render. They only close over B,
// which is an import.
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '5px 7px', fontSize: 12,
  borderRadius: 3, border: '1px solid #ccc', background: '#fff', textAlign: 'right',
};
const calcStyle = {
  ...inputStyle, background: '#f5f9ff', color: B.cyanDark,
  fontWeight: 500, border: '1px solid #d0e4f7',
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 3 };
const subLabel   = { fontSize: 10, color: '#777', marginBottom: 2 };
const noteBase   = { fontSize: 11, lineHeight: 1.5 };

const sectionHdr = (title, amber) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: amber || B.cyanDark, textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '6px 10px',
    background: amber ? B.amberLight : B.cyanLight,
    borderRadius: 4, marginBottom: 8, marginTop: 12,
  }}>{title}</div>
);

// A computed cell. Visually distinct from an input because it cannot be typed
// into, which the old form already got right and is worth keeping.
const Computed = ({ label, value, tone }) => (
  <div>
    <div style={{ ...subLabel, color: tone ? tone.fg : B.cyanDark, fontWeight: 600 }}>{label}</div>
    <div style={{ ...calcStyle,
                  ...(tone ? { background: tone.bg, borderColor: tone.border, color: tone.fg } : {}) }}>
      {fmt(value)}
    </div>
  </div>
);

// ── the tie-out ───────────────────────────────────────────────────────────
// The reason the detail is worth showing at all. An aging that does not add up
// to the line it describes means either the aging is partial or the gross is
// wrong, and both are worth knowing BEFORE the entry is saved and every
// downstream figure inherits it.
//
// Informational, never blocking. Borrowers legitimately age a subset of the
// book, and a form that refuses to save over a $40 rounding difference is a
// form people learn to work around.
const TieOut = ({ reported, gross, noun }) => {
  if (!(reported > 0)) {
    return <span style={{ ...noteBase, color: '#777' }}>
      No {noun} entered — nothing to check against the gross amount.
    </span>;
  }
  if (!(gross > 0)) {
    return <span style={{ ...noteBase, color: '#777' }}>
      {noun} totals {fmt(reported)}. No gross amount on this line to compare it against.
    </span>;
  }
  const diff = reported - gross;
  if (Math.abs(diff) < 0.01) {
    return <span style={{ ...noteBase, color: '#0F6E56', fontWeight: 600 }}>
      Ties to the gross amount of {fmt(gross)}.
    </span>;
  }
  return <span style={{ ...noteBase, color: '#BA7517', fontWeight: 600 }}>
    {fmt(Math.abs(diff))} {diff > 0 ? 'more than' : 'less than'} the gross amount
    of {fmt(gross)} — the aging and the line disagree.
  </span>;
};

// A bucket grid plus its total and tie-out. Used by both A/R and inventory,
// because they are the same shape and two copies of a layout drift the same way
// two copies of a rule do.
const BucketBlock = ({ title, tint, buckets, line, onField, total, totalLabel, gross, noun, totalTone }) => (
  <div style={{ background: tint.bg, border: '0.5px solid ' + tint.border,
                borderRadius: 6, padding: '10px 12px', marginTop: 10 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: tint.fg, letterSpacing: '0.05em',
                  textTransform: 'uppercase', marginBottom: 8 }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                  gap: '8px 12px' }}>
      {buckets.map(([label, key]) => (
        <div key={key}>
          <div style={subLabel}>{label}</div>
          <FInputNum value={line[key]} onChange={onField(key)} />
        </div>
      ))}
      <Computed label={totalLabel} value={total} tone={totalTone} />
    </div>
    <div style={{ marginTop: 8 }}>
      <TieOut reported={total} gross={gross} noun={noun} />
    </div>
  </div>
);

export const CollateralSpreadsheet = ({ headerId, loanid, prefill, onDone }) => {
    const { api, closeModal } = useApp();

    const blankHeader = {
      loanid, asofdate: '',
      grossavailabilityreserves: '', overadvanceallowance: '',
      aggregateinventorysublimit: '', aggregateinventoryreserve: '',
      apaging0to30: '', apaging31to60: '', apaging61to90: '', apaging90plus: ''
    };

    const blankLine = (type = '') => ({
      _tempId: 'new_' + Math.random().toString(36).slice(2),
      collateralheaderid: headerId || '',
      loanid,
      collateraltype: type,
      grossamount: '', ineligibleamount: '', advancerate: '',
      otherreserves: '', sublimit: '',
      araging0to30: '', araging31to60: '', araging61to90: '',
      araging90plus: '', araging120plus: '',
      invrawmaterials: '', invworkinprogress: '', invfinishedgoods: '',
      invgeneral: '', invother: ''
    });

    // A prefill (e.g. mapped from an approved BBC) seeds the form for review —
    // nothing is saved until the user clicks Save Entry.
    const [header, setHeader]   = useState(prefill?.header ? { ...blankHeader, ...prefill.header } : blankHeader);
    const [lines, setLines]     = useState(prefill?.lines?.length
      ? prefill.lines.map(l => ({ ...blankLine(l.collateraltype), ...l }))
      : [blankLine()]);
    const [loading, setLoading] = useState(!!headerId);
    const [saving, setSaving]   = useState(false);
    const [err, setErr]         = useState(null);

    // Load existing header + lines if editing
    useEffect(() => {
      if (!headerId) return;
      setLoading(true);
      api.get('getCollateralHeaders?id=' + headerId).then(data => {
        setHeader(data.header);
        setLines(data.lines.length ? data.lines : [blankLine()]);
        setLoading(false);
      }).catch(e => { setErr(e.message); setLoading(false); });
    }, [headerId]);

    const setH = key => val => setHeader(p => ({ ...p, [key]: val }));
    const setLine = (idx, key) => val => setLines(p => {
      const updated = [...p];
      updated[idx] = { ...updated[idx], [key]: val };
      return updated;
    });

    const addLine = () => setLines(p => [...p, blankLine()]);
    const removeLine = idx => setLines(p => p.filter((_, i) => i !== idx));

    const handleSave = async () => {
      setSaving(true); setErr(null);
      try {
        let hId = header.id;

        // Save header
        const hResult = await api.post('saveCollateralHeader', {
          ...header,
          id: hId || undefined,
          loanid,
          carryforward: false  // carry-forward only on initial creation from the list
        });
        if (!hId) hId = hResult.id;

        // Save each line
        for (const line of lines) {
          if (!line.collateraltype) continue; // skip blank lines
          await api.post('saveCollateralLine', {
            ...line,
            id: line.id || undefined,
            collateralheaderid: hId,
            loanid,
          });
        }

        if (onDone) onDone();
        closeModal();
      } catch(e) {
        setErr(e.message);
        setSaving(false);
      }
    };

    // Computed totals
    const headerCalc = calcCollateralHeader(header, lines.map(l => ({
      ...l,
      collateraltype: l.collateraltype
    })));

    const hasInventoryLine = lines.some(l => l.collateraltype === 'Inventory');

    if (loading) return <Modal title="Collateral Entry" onClose={closeModal} wide><Spinner /></Modal>;

    return (
      <Modal title={headerId ? `Edit Collateral — ${fmtDate(header.asofdate)}` : (prefill ? 'Review Collateral Entry — from Borrowing Base' : 'New Collateral Entry')}
            onClose={closeModal} wide>

        {/* ── Entry details ────────────────────────────────────────────────
            As-of date and the availability-level reserves. The two aggregate
            inventory fields are split out below rather than sitting in the same
            undifferentiated row of five: they are not properties of the entry,
            they are a cap applied to every inventory line TOGETHER and after
            each line's own sublimit, and nothing on the old form said so. */}
        {sectionHdr('Entry Details')}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                      gap: '0 16px', marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>As-of Date *</div>
            <input type="date" value={header.asofdate?.slice(0,10)||''} onChange={e=>setH('asofdate')(e.target.value)}
              style={{ ...inputStyle, textAlign: 'left' }} />
          </div>
          <div>
            <div style={labelStyle}>Gross Avail Reserves</div>
            <FInputNum value={header.grossavailabilityreserves} onChange={setH('grossavailabilityreserves')} />
          </div>
          <div>
            <div style={labelStyle}>Over Advance Allowance</div>
            <FInputNum value={header.overadvanceallowance} onChange={setH('overadvanceallowance')} />
          </div>
        </div>

        <div style={{ background: '#f7fbf7', border: '0.5px solid #cfe8cf', borderRadius: 6,
                      padding: '10px 12px', marginBottom: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', textTransform: 'uppercase',
                        letterSpacing: '0.05em', marginBottom: 2 }}>Aggregate Inventory Caps</div>
          <div style={{ ...noteBase, color: '#555', marginBottom: 8 }}>
            Applied to all inventory lines together, after each line's own sublimit.
            A sublimit of zero means no cap.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: '0 16px' }}>
            <div>
              <div style={labelStyle}>Agg Inventory Sublimit</div>
              <FInputNum value={header.aggregateinventorysublimit} onChange={setH('aggregateinventorysublimit')} />
            </div>
            <div>
              <div style={labelStyle}>Agg Inventory Reserve</div>
              <FInputNum value={header.aggregateinventoryreserve} onChange={setH('aggregateinventoryreserve')} />
            </div>
            {hasInventoryLine && (
              <>
                <Computed label="Inventory available ⟹" value={headerCalc.totalAvailableInventory} />
                <Computed label="Net after caps ⟹" value={headerCalc.netAvailableInventory}
                          tone={{ bg: '#e0f5e0', border: '#a0d8a0', fg: '#0F6E56' }} />
              </>
            )}
          </div>
        </div>

        {/* ── Collateral lines ─────────────────────────────────────────────
            One card per line. The old ten-column table scrolled sideways in a
            modal and pushed the detail into orphaned full-width rows below the
            line it belonged to; that only reads as one record while the detail
            is hidden, which is exactly what this change stops doing. */}
        {sectionHdr('Collateral Lines')}

        {lines.map((line, idx) => {
          const calc  = calcCollateralLine(line);
          const isAR  = line.collateraltype === 'Accounts Receivable';
          const isInv = line.collateraltype === 'Inventory';
          const gross = parseFloat(line.grossamount || 0) || 0;
          // A line that exists in the database cannot be taken off the entry
          // from here — see the button's title. Only unsaved lines can be
          // dropped, because dropping one is just not sending it.
          const isSaved = !!line.id;

          return (
            <div key={line.id || line._tempId}
                 style={{ border: '0.5px solid ' + B.cyan, borderRadius: 8, padding: '12px 14px',
                          marginBottom: 12, background: '#fff' }}>

              {/* line header: what it is, what it is worth, and the control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                            flexWrap: 'wrap', marginBottom: 10 }}>
                <select value={line.collateraltype || ''} onChange={e => setLine(idx, 'collateraltype')(e.target.value)}
                  style={{ ...inputStyle, textAlign: 'left', fontSize: 12, width: 200, flex: '0 0 auto' }}>
                  <option value="">— Select type —</option>
                  {COLLATERAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ ...subLabel, marginBottom: 0 }}>Collateral value</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: B.cyanDark }}>
                      {fmt(calc.collateralvalue)}
                    </div>
                  </div>
                  <button onClick={() => removeLine(idx)}
                    disabled={isSaved || lines.length <= 1}
                    title={isSaved
                      ? 'A saved line cannot be removed here. There is no delete endpoint for a collateral line, so taking it off the form would simply leave it in the database and it would reappear on the next open. Delete the whole entry from the Collateral tab, or set this line to zero.'
                      : lines.length <= 1
                        ? 'An entry needs at least one line.'
                        : 'Remove this unsaved line'}
                    style={{ background: 'none',
                             border: '0.5px solid ' + ((isSaved || lines.length <= 1) ? '#ccc' : '#E24B4A'),
                             borderRadius: 4, padding: '4px 8px', fontSize: 11,
                             color: (isSaved || lines.length <= 1) ? '#aaa' : '#A32D2D',
                             cursor: (isSaved || lines.length <= 1) ? 'default' : 'pointer' }}>✕</button>
                </div>
              </div>

              {/* the calculation, left to right, same chain as before */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
                            gap: '8px 12px' }}>
                <div>
                  <div style={subLabel}>Gross Amt</div>
                  <FInputNum value={line.grossamount} onChange={setLine(idx, 'grossamount')} />
                </div>
                <div>
                  <div style={subLabel}>Ineligible</div>
                  <FInputNum value={line.ineligibleamount} onChange={setLine(idx, 'ineligibleamount')} />
                </div>
                <Computed label="Eligible ⟹" value={calc.eligible} />
                <div>
                  <div style={subLabel}>Adv Rate %</div>
                  <input type="number" value={line.advancerate || ''} onChange={e => setLine(idx, 'advancerate')(e.target.value)}
                    placeholder="0.00" style={{ ...inputStyle, fontSize: 12 }} />
                </div>
                <Computed label="Pre-Dilution ⟹" value={calc.valuepredilution} />
                <div>
                  <div style={subLabel}>Reserves</div>
                  <FInputNum value={line.otherreserves} onChange={setLine(idx, 'otherreserves')} />
                </div>
                <div>
                  <div style={subLabel}>Sublimit</div>
                  <FInputNum value={line.sublimit} onChange={setLine(idx, 'sublimit')} />
                </div>
                <Computed label="Coll Value ⟹" value={calc.collateralvalue} />
              </div>

              {/* THE DETAIL, ALWAYS. No toggle. */}
              {isAR && (
                <BucketBlock
                  title="A/R Aging"
                  tint={{ bg: '#fffdf2', border: '#ebd3a3', fg: B.amberDark }}
                  buckets={AR_BUCKETS} line={line} onField={key => setLine(idx, key)}
                  total={calc.totalaraging} totalLabel="Total A/R Aging ⟹"
                  gross={gross} noun="A/R aging" />
              )}

              {isInv && (
                <BucketBlock
                  title="Inventory Breakout"
                  tint={{ bg: '#f7fbf7', border: '#cfe8cf', fg: '#0F6E56' }}
                  buckets={INV_BUCKETS} line={line} onField={key => setLine(idx, key)}
                  total={calc.totalinventory} totalLabel="Total Inventory ⟹"
                  gross={gross} noun="inventory breakout"
                  totalTone={{ bg: '#e0f5e0', border: '#a0d8a0', fg: '#0F6E56' }} />
              )}
            </div>
          );
        })}

        <div style={{ marginBottom: 12 }}>
          <Btn onClick={addLine} variant="primary">+ Add Line</Btn>
        </div>

        {/* ── Total ────────────────────────────────────────────────────────
            Out of the table footer and into a strip of its own. It was the
            last row of a sideways-scrolling table, which is the easiest place
            on a form to miss the one number the form exists to produce. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                      background: B.cyanLight, border: '0.5px solid ' + B.cyan,
                      borderRadius: 8, padding: '12px 16px', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark,
                          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Total Collateral Value
            </div>
            {hasInventoryLine && (
              <div style={{ ...noteBase, color: '#555', marginTop: 2 }}>
                Inventory available {fmt(headerCalc.totalAvailableInventory)} → net of the aggregate
                caps {fmt(headerCalc.netAvailableInventory)}.
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 20, fontWeight: 700, color: B.cyanDark }}>
            {fmt(headerCalc.totalCollateralValue)}
          </div>
        </div>

        {/* ── A/P aging ────────────────────────────────────────────────────
            Header-level and across all types, which the section title has
            always said and the placement now supports: it sits after the lines
            because it does not belong to any one of them. */}
        {sectionHdr('AP Aging (All Types)', '#603700')}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '0 16px', marginBottom: 16 }}>
          {AP_BUCKETS.map(([label, key]) => (
            <div key={key}>
              <div style={labelStyle}>AP Aging {label}</div>
              <FInputNum value={header[key]} onChange={setH(key)} />
            </div>
          ))}
          <div>
            <div style={{ ...labelStyle, color: B.amberDark }}>Total AP Aging ⟹</div>
            <div style={{ ...calcStyle, padding: '5px 7px', background: B.amberLight,
                          borderColor: B.amberMid, color: B.amberDark }}>
              {fmt(headerCalc.totalAPAging)}
            </div>
          </div>
        </div>

        {err && <div style={{ color: '#993C1D', fontSize: 12, marginBottom: 8 }}>⚠️ {err}</div>}
        {headerId && <NotesPanel scope="record" entitytype="Collateral" entityid={headerId} accountid={header.accountid || lines[0]?.accountid} loanid={loanid} source="Collateral" />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <Btn onClick={closeModal}>Cancel</Btn>
          <Btn variant="primary" disabled={saving || !header.asofdate} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Entry'}
          </Btn>
        </div>
      </Modal>
    );
  };
